import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { z } from "zod";
import { immer } from "zustand/middleware/immer";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  getCustomFieldNames,
  QuoteSettingsResponseSchema,
  QuoteSettingsSchema,
  QuoteTemplateId,
  CustomFieldsSchema,
  QuotePriceRounding,
  ItemOverrideFieldSelection,
  type QuoteAdvancedSettingsUpdate,
  type QuoteSettings,
  type SettingsLockedBy,
} from "@forgepoint/shared";

const UpdateQuoteSettingsResponseSchema = z.object({
  settings: QuoteSettingsSchema,
});

interface QuoteSettingsState {
  settings: QuoteSettings | null;
  lockedBy: SettingsLockedBy;
  isLoading: boolean;
  error: string | null;
  fetchSettings: (options?: { force?: boolean }) => Promise<void>;
  updateSettings: (
    params: QuoteAdvancedSettingsUpdate & {
      termsAndConditions?: string;
      paymentTerms?: string;
      deliveryTerms?: string;
      quoteFormattingUserGuidance?: string;
      publicQuoteTemplate?: QuoteTemplateId;
      quotePriceRounding?: QuotePriceRounding | null;
      quoteFooter?: string;
      useSignatures?: boolean;
      showTotals?: boolean;
      showTotalWeight?: boolean;
      aggregatePriceListBreaks?: boolean;
      priceListBreakGroupField?: string | null;
      showConfidenceScore?: boolean;
      enableCoverLetter?: boolean;
      showLocationField?: boolean;
      allowItemFieldOverrides?: boolean;
      itemOverrideFields?: ItemOverrideFieldSelection;
      allowLineUnitOverride?: boolean;
      allowPerQuoteDocumentEditing?: boolean;
      customFieldsSchema?: CustomFieldsSchema | null;
      lineItemCustomFieldsSchema?: CustomFieldsSchema | null;
      quoteNumberPadWidth?: number | null;
      quoteNumberPrefix?: string | null;
      defaultQuoteDocumentTemplateId?: string | null;
    },
  ) => Promise<boolean>;
}

let latestFetchRequestId = 0;

/** Zustand store for organization-level quote settings and their ERP locks. */
export const useQuoteSettingsStore = create<QuoteSettingsState>()(
  immer((set, get) => ({
    settings: null,
    lockedBy: {},
    isLoading: false,
    error: null,

    /**
     * Fetches the settings and ERP locks, applying only the latest request's response.
     * @param options - `force` bypasses the in-flight dedupe so a refetch always runs
     * @returns Resolves once the fetch settles
     */
    fetchSettings: async (options) => {
      // Dedupe concurrent fetches: many components (e.g. every board card) call
      // this on mount while settings are still null; without this guard each
      // would fire its own GET and risk exhausting the request rate limit.
      if (get().isLoading && !options?.force) {
        return;
      }
      const requestId = ++latestFetchRequestId;
      set({ isLoading: true, error: null });

      try {
        const response = await fetchApi("quote-settings", "GET");
        if (requestId !== latestFetchRequestId) {
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch quote settings");
        }

        const parsed = QuoteSettingsResponseSchema.safeParse(response.data);
        if (!parsed.success) {
          captureException(parsed.error);
          set({ error: "Invalid quote settings response", isLoading: false });
          return;
        }

        set({
          settings: parsed.data.settings,
          lockedBy: parsed.data.lockedBy,
          isLoading: false,
        });
      } catch (err) {
        if (requestId !== latestFetchRequestId) {
          return;
        }
        const normalizedError =
          err instanceof Error ? err : new Error("An unknown error occurred");
        captureException(normalizedError);
        set({ error: normalizedError.message, isLoading: false });
      }
    },

    updateSettings: async (params) => {
      set({ isLoading: true, error: null });

      try {
        const response = await fetchApi("quote-settings", "POST", {
          body: params,
        });

        if (!response.ok) {
          throw new Error(response.error || "Failed to update quote settings");
        }

        const parsed = UpdateQuoteSettingsResponseSchema.safeParse(
          response.data,
        );
        if (!parsed.success) {
          captureException(parsed.error);
          set({ error: "Invalid quote settings response", isLoading: false });
          return false;
        }

        set({ settings: parsed.data.settings, isLoading: false });
        return true;
      } catch (err) {
        const normalizedError =
          err instanceof Error ? err : new Error("An unknown error occurred");
        captureException(normalizedError);
        set({ error: normalizedError.message, isLoading: false });
        return false;
      }
    },
  })),
);

/**
 * Reads `quoteNumberPadWidth` from settings and ensures the settings have been
 * fetched at least once. Safe to call from any component — skips the fetch when
 * settings are already loaded.
 * @returns The configured pad width, or null when not yet loaded / not configured
 */
export function useQuoteNumberPadWidth(): number | null {
  const padWidth = useQuoteSettingsStore(
    (state) => state.settings?.quoteNumberPadWidth ?? null,
  );
  const settings = useQuoteSettingsStore((state) => state.settings);
  const fetchSettings = useQuoteSettingsStore((state) => state.fetchSettings);

  useEffect(() => {
    if (!settings) {
      fetchSettings();
    }
  }, [settings, fetchSettings]);

  return padWidth;
}

/**
 * Reads the org's quote-level custom field names (in configured order),
 * ensuring settings have been fetched at least once. Safe to call from any
 * component — skips the fetch when settings are already loaded.
 * @returns The custom field names, empty until settings load or when none exist
 */
export function useQuoteCustomFieldNames(): string[] {
  const schema = useQuoteSettingsStore(
    (state) => state.settings?.customFieldsSchema ?? null,
  );
  const settings = useQuoteSettingsStore((state) => state.settings);
  const fetchSettings = useQuoteSettingsStore((state) => state.fetchSettings);

  useEffect(() => {
    if (!settings) {
      fetchSettings();
    }
  }, [settings, fetchSettings]);

  return useMemo(() => getCustomFieldNames(schema), [schema]);
}

/**
 * Reads the org's line-item custom field names (in configured order),
 * ensuring settings have been fetched at least once. Safe to call from any
 * component - skips the fetch when settings are already loaded.
 * @returns The custom field names, empty until settings load or when none exist
 */
export function useLineItemCustomFieldNames(): string[] {
  const schema = useQuoteSettingsStore(
    (state) => state.settings?.lineItemCustomFieldsSchema ?? null,
  );
  const settings = useQuoteSettingsStore((state) => state.settings);
  const fetchSettings = useQuoteSettingsStore((state) => state.fetchSettings);

  useEffect(() => {
    if (!settings) {
      fetchSettings();
    }
  }, [settings, fetchSettings]);

  return useMemo(() => getCustomFieldNames(schema), [schema]);
}

/**
 * Reads `quoteNumberPrefix` from settings and ensures the settings have been
 * fetched at least once. Safe to call from any component — skips the fetch when
 * settings are already loaded.
 * @returns The configured prefix, or null when not yet loaded / not configured
 */
export function useQuoteNumberPrefix(): string | null {
  const prefix = useQuoteSettingsStore(
    (state) => state.settings?.quoteNumberPrefix ?? null,
  );
  const settings = useQuoteSettingsStore((state) => state.settings);
  const fetchSettings = useQuoteSettingsStore((state) => state.fetchSettings);

  useEffect(() => {
    if (!settings) {
      fetchSettings();
    }
  }, [settings, fetchSettings]);

  return prefix;
}

/**
 * Reads whether the org assigns locations to quotes and opportunities, ensuring
 * settings have been fetched at least once. Safe to call from any component —
 * skips the fetch when settings are already loaded.
 * @returns True when the location field is enabled, false until settings load
 */
export function useShowLocationField(): boolean {
  const showLocationField = useQuoteSettingsStore(
    (state) => state.settings?.showLocationField ?? false,
  );
  const settings = useQuoteSettingsStore((state) => state.settings);
  const fetchSettings = useQuoteSettingsStore((state) => state.fetchSettings);

  useEffect(() => {
    if (!settings) {
      fetchSettings();
    }
  }, [settings, fetchSettings]);

  return showLocationField;
}
